/**
 * Profile — My Grid: owned parcel summary (Macro #23 cockpit tab).
 *
 * Backend-authoritative owned-land snapshot (parcel count, total slots,
 * connected clusters, largest cluster) + a per-parcel list with tier, area and
 * edge-neighbor counts. Read-only; never fabricates land or value.
 */
import { useCallback, useEffect, useState } from "react";
import { ParcelUtilityError, parcelUtilityApi, type SelfUtility } from "../../lib/parcel-utility-api";
import { Card, StatTile } from "./primitives";
import { useI18n } from "../../i18n";

const TIER_LABEL: Record<string, string> = {
  GRID_ZERO: "Grid Zero", VIP_CORE: "VIP Core", PREMIUM: "Premium", RESERVE: "Reserve", STANDARD: "Standard",
};

export function MyGridPanel() {
  const { t } = useI18n();
  const [state, setState] = useState<"loading" | "auth" | "error" | "ok">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<SelfUtility | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setState("loading"); setErr(null);
    try {
      const d = await parcelUtilityApi.self(signal);
      setData(d); setState("ok");
    } catch (e) {
      if (e instanceof ParcelUtilityError && e.status === 401) { setState("auth"); return; }
      setErr(e instanceof Error ? e.message : t("profile.myGrid.failedToLoad")); setState("error");
    }
  }, []);

  useEffect(() => { const c = new AbortController(); void load(c.signal); return () => c.abort(); }, [load]);

  if (state === "loading") return <div data-testid="my-grid-loading" className="p-8 text-center font-mono text-sm text-white/50">{t("profile.myGrid.loading")}</div>;
  if (state === "auth") return (
    <div data-testid="my-grid-auth-required" className="mx-auto max-w-md p-8 text-center">
      <a href="#auth" className="inline-flex rounded-full border border-teal-300/40 bg-teal-400/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-200 no-underline">{t("profile.myGrid.signIn")}</a>
    </div>
  );
  if (state === "error" || !data) return (
    <div data-testid="my-grid-error" className="mx-auto max-w-md p-8 text-center text-red-300">
      <p className="mb-4">{err || t("profile.myGrid.unavailable")}</p>
      <button onClick={() => void load()} className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80">{t("profile.myGrid.retry")}</button>
    </div>
  );

  if (!data.owns_land) {
    return (
      <Card title={t("profile.myGrid.title")} testId="my-grid-panel">
        <p data-testid="my-grid-empty" className="text-sm text-white/50">{t("profile.myGrid.empty")}</p>
      </Card>
    );
  }

  return (
    <div data-testid="my-grid-panel" className="space-y-6">
      <Card title={t("profile.myGrid.title")} testId="my-grid-summary">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label={t("profile.myGrid.parcels")} value={data.summary.parcel_count} testId="mygrid-parcel-count" />
          <StatTile label={t("profile.myGrid.totalSlots")} value={data.summary.total_area} testId="mygrid-total-area" />
          <StatTile label={t("profile.myGrid.clusters")} value={data.summary.connected_cluster_count} testId="mygrid-cluster-count" />
          <StatTile label={t("profile.myGrid.largestCluster")} value={data.summary.largest_cluster_size} testId="mygrid-largest-cluster" sub={t("profile.myGrid.slots", { n: data.summary.largest_cluster_area })} />
        </div>
      </Card>

      <Card title={t("profile.myGrid.parcels")} testId="my-grid-parcels">
        <ul className="space-y-2">
          {data.parcels.map((p) => (
            <li key={p.parcel_id} data-testid={`mygrid-parcel-${p.parcel_id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <span className="font-mono text-sm text-white/85">#{p.parcel_id}</span>
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-wide text-white/55">{TIER_LABEL[p.tier] ?? p.tier}</span>
              <span className="text-[11px] text-white/45">{t("profile.myGrid.parcelMeta", { w: p.geometry.w, h: p.geometry.h, area: p.area })}</span>
              <span className="ml-auto text-[11px] text-white/45" data-testid={`mygrid-parcel-${p.parcel_id}-neighbors`}>{t("profile.myGrid.edgeNeighbors", { n: p.edge_neighbor_count })}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
